import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPlatform } from '@platform/index';
import { AddContentMenu } from '../home/components/AddContentMenu';
import { IconMenu, IconSearch } from './icons';
import { useShellLayout } from './ShellLayoutContext';
import { UserMenu } from './UserMenu';

interface GlobalHeaderProps {
  mobile: boolean;
  onOpenMobileNav?: () => void;
}

export function GlobalHeader({ mobile, onOpenMobileNav }: GlobalHeaderProps) {
  const platform = getPlatform();
  const { workspaceTitle, contextPanel, detailPanel, setContextOpenMobile, setDetailOpenMobile } =
    useShellLayout();
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const searchId = useId();

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === 'Escape') {
        setSearchOpen(false);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (searchOpen) {
      inputRef.current?.focus();
    }
  }, [searchOpen]);

  return (
    <header className="global-header" data-testid="global-header">
      <div className="global-header__left">
        {mobile && (
          <button
            type="button"
            className="icon-btn"
            aria-label="Open primary navigation"
            data-testid="header-mobile-menu"
            onClick={onOpenMobileNav}
          >
            <IconMenu />
          </button>
        )}
        <Link to="/" className="global-header__brand" aria-label="Spox home">
          <img
            className="global-header__logo"
            src="/brand/logo/horizontal/full-colour/spox-horizontal-transparent.png"
            alt="Spox"
            width={180}
            height={46}
          />
        </Link>
        {workspaceTitle && !mobile && (
          <div className="global-header__workspace" data-testid="workspace-title">
            <span className="global-header__workspace-sep" aria-hidden="true" />
            <span className="global-header__workspace-title">{workspaceTitle}</span>
          </div>
        )}
      </div>

      <div className="global-header__center">
        <button
          type="button"
          className="global-search-trigger"
          data-testid="global-search-trigger"
          aria-haspopup="dialog"
          aria-expanded={searchOpen}
          aria-controls={searchId}
          onClick={() => setSearchOpen(true)}
        >
          <IconSearch />
          <span className="global-search-trigger__label">Search Spox</span>
          <kbd className="global-search-trigger__kbd">⌘K</kbd>
        </button>
      </div>

      <div className="global-header__right">
        <AddContentMenu />
        {mobile && contextPanel && (
          <button
            type="button"
            className="btn ghost compact"
            onClick={() => setContextOpenMobile(true)}
            data-testid="open-context-sheet"
          >
            Context
          </button>
        )}
        {mobile && detailPanel && (
          <button
            type="button"
            className="btn ghost compact"
            onClick={() => setDetailOpenMobile(true)}
            data-testid="open-detail-sheet"
          >
            Detail
          </button>
        )}
        <div className="platform-chip" data-testid="platform-label">
          {platform.getAppLabel()}
        </div>
        <UserMenu />
      </div>

      {searchOpen && (
        <div
          className="global-search-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${searchId}-title`}
          id={searchId}
          data-testid="global-search-dialog"
        >
          <div className="global-search-dialog__backdrop" onClick={() => setSearchOpen(false)} />
          <div className="global-search-dialog__panel">
            <h2 id={`${searchId}-title`} className="sr-only">
              Search Spox
            </h2>
            <div className="global-search-dialog__field">
              <IconSearch />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Spox…"
                aria-label="Search Spox"
              />
            </div>
            <p className="muted global-search-dialog__hint">
              Command search is a shell contract in SPX-003A. Full indexed search expands in later
              stages.
              {query.trim() ? ` Query captured: “${query.trim()}”.` : ''}
            </p>
            <div className="row">
              <button type="button" className="btn secondary compact" onClick={() => setSearchOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
